import { useEffect, useMemo } from "react";

export interface DataLoaderProps<T> {
    fetchKmsData: () => T;
    onLoaded: (response: T) => void;
}

/**
 * DataLoader component
 *
 * This component is responsible for fetching data and calling the `onLoaded` callback with the fetched data.
 * It uses `useMemo` to memoize the result of the `fetchKmsData` function and `useEffect` to call the `onLoaded` callback whenever the fetched data changes.
 *
 * Props:
 * - fetchKmsData: Optional function to fetch data.
 * - onLoaded: Callback function to handle the loaded  data.
 *
 * Behavior:
 * - Memoizes the result of the `fetchKmsData` function to ensure it is only called once and the result is cached.
 * - Calls the `onLoaded` callback with the fetched data whenever the fetched data changes.
 *
 * Render:
 * - This component does not render any visible UI. It is only responsible for fetching and providing data.
 */
export const DataLoader = <T,>({ fetchKmsData, onLoaded }: DataLoaderProps<T>) => {
    // used to memoize the result of the fetchKmsData function.
    // ensures that the fetchKmsData function is only called once and the result is cached.
    const loadedChunk: T | undefined = useMemo(() => {
        return fetchKmsData();
    }, [fetchKmsData]);

    // useEffect to call the onLoaded callback with the loadedChunk data
    // whenever the loadedChunk changes.
    useEffect(() => {
        if (!loadedChunk) {
            return;
        }
        onLoaded(loadedChunk);
    }, [loadedChunk]);

    return null; // no visible UI for this component, its only the chunks fetcher
};
